import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import { getItemsHavingMetadata, recategorizeItems, removeTagFromItems } from "../data/settings";
import CacheContext from "../../cache/CacheContext";
import bus from '../../utils/EventBus'
import EventNames from '../../utils/EventNames'

export default function ReassignmentDialog({
  open,
  onClose: propsOnClose,
  label,                // value of the chip
  itemType,             // "tags" | "categories"
  entityType,           // "articles" | "resources"
  reassignmentOptions = [] // array of strings
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [selectedAction, setSelectedAction] = useState(""); // 'untag' | 'reassign'

  const cacheContext = useContext(CacheContext);

  const entityTypeLabel =
    entityType === "resources" ? "Resource"
    : entityType === "articles" ? "Article"
    : "";

  const itemTypeLabel =
    itemType === "tags" ? "Tag"
    : itemType === "categories" ? "Category"
    : "";

  // Reset state on close
  const onClose = () => {
    setNewValue("");
    setItems([]);
    setSelectedAction("");
    propsOnClose();
  };

  // Fetch items from Graph API (stubbed)
  async function fetchItemsFromGraph(label, itemType, entityType) {
    const results = await getItemsHavingMetadata({
      tag: itemType === "tags" ? label : undefined,
      category: itemType === "categories" ? label : undefined,
      list: entityType,
      fetchListItems: true
    }, cacheContext);
    return results;
  }

  useEffect(() => {
    if (open && label) {
      setLoading(true);
      fetchItemsFromGraph(label, itemType, entityType)
        .then(data => setItems(data))
        .finally(() => setLoading(false));
    }
  }, [open, label, itemType, entityType]);

  // Preselect "reassign" for categories when dialog opens
  useEffect(() => {
    if (open && itemType === "categories") {
      setSelectedAction("recategorize");
    }
  }, [open, itemType]);

  // Stubbed update logic
  async function removeOrReassignTags() {
    bus.emit(EventNames.SHOW_PROGRESS_MODAL)
    for await (const progress of removeTagFromItems(
      items,
      label, //tag
      newValue, //newTag
      entityType,
      cacheContext
    )) {
      bus.emit(EventNames.PROGRESS_MODAL_PROGRESS, {
        completed: progress.total,
        progress: progress.completed,
      })
    }
    bus.emit(EventNames.HIDE_PROGRESS_MODAL)
    onClose();
  }

  async function recategorize() {
    bus.emit(EventNames.SHOW_PROGRESS_MODAL)
    for await (const progress of recategorizeItems(
      items,
      label, //category
      newValue, //newCategory
      entityType,
      cacheContext
    )) {
      bus.emit(EventNames.PROGRESS_MODAL_PROGRESS, {
        completed: progress.total,
        progress: progress.completed,
      })
    }
    bus.emit(EventNames.HIDE_PROGRESS_MODAL)
    onClose();
  }

  // Handle confirm button click
  async function handleConfirm() {
    console.log('selectedAction', selectedAction)
    if (selectedAction === "untag" || selectedAction === "reassign") {
      await removeOrReassignTags();
    } else if (selectedAction === "recategorize") {
      await recategorize();
    }
  }

  // Confirm button label and disable logic
  const confirmLabel =
    selectedAction === "untag"
      ? `Confirm Un-Tag Listed ${entityTypeLabel}s`
      : selectedAction === "reassign"
        ? `Confirm recategorize Listed ${entityTypeLabel}s`
        : "Confirm recategorize listed resources";

  const confirmDisabled =
    !selectedAction ||
    items.length === 0 ||
    (selectedAction === "reassign" && !newValue)
    || (selectedAction === "recategorize" && !newValue);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {itemType === "categories"
          ? `Bulk Category Reassignment (${entityTypeLabel}s)`
          : `Bulk Tag Change (${entityTypeLabel}s)`}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Left panel: items list */}
          <Box sx={{ flex: 1, borderRight: "1px solid #ccc", pr: 2 }}>
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              items.length ? (
                <Box
                  sx={{
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid #ccc"
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{
                          fontSize: ".8em",
                          textAlign: "left",
                          padding: "4px",
                          background: "#f5f5f5",
                          borderBottom: "1px solid #ccc"
                        }}>
                          {items.length} {entityTypeLabel}s with the {label} {itemTypeLabel}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.id}>
                          <td style={{
                            fontSize: ".8em",
                            padding: "4px",
                            borderBottom: "1px solid #eee"
                          }}>
                            {item.title}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              ) : (
                "No items"
              )
            )}
          </Box>

          {/* Right panel: controls */}
          <Box sx={{ flex: 1 }}>
            {itemType === "tags" ? (
              <>
                <Typography>
                  Select an action for these {entityTypeLabel}s:
                </Typography>
                <RadioGroup
                  value={selectedAction}
                  onChange={(e) => setSelectedAction(e.target.value)}
                >
                  <FormControlLabel
                    value="untag"
                    control={<Radio />}
                    label={`Remove Tag from all listed ${entityTypeLabel}s`}
                  />
                  <FormControlLabel
                    value="reassign"
                    control={<Radio />}
                    label="Reassign to a new tag"
                  />
                </RadioGroup>

                {selectedAction === "reassign" && (
                  <Select
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mt: 1 }}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>(No selection)</em>
                    </MenuItem>
                    {reassignmentOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </>
            ) : (
              <>
                <Typography>
                  Select a new category to reassign all {entityTypeLabel}s:
                </Typography>
                <Select
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select new category</em>
                  </MenuItem>
                  {reassignmentOptions.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleConfirm}
          disabled={confirmDisabled}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}